"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X } from "lucide-react";

export default function PrivacyPage() {
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light") {
      setIsLightTheme(true);
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isLightTheme;
    setIsLightTheme(newTheme);
    localStorage.setItem("theme", newTheme ? "light" : "dark");
    if (newTheme) {
      document.documentElement.classList.add("light");
    } else {
      document.documentElement.classList.remove("light");
    }
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/contact", label: "Contact" },
    { href: "/terms", label: "Terms" },
    { href: "/privacy", label: "Privacy" },
    { href: "/cookies", label: "Cookies" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col landing-theme">
      {/* ============================================ */}
      {/* NAVIGATION - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - proportional sizing */}
            <Link href="/" className="flex items-center">
              <span className="text-xl portrait:text-[clamp(1.25rem,2.5vw,1.75rem)] font-bold text-foreground tracking-tight">Lateral</span>
            </Link>

            {/* Separator - Desktop only (lg+) */}
            <div className="hidden lg:block separator-line mx-6" />

            {/* Menu Items - Desktop only (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-6 portrait:gap-[clamp(1.5rem,2vw,2rem)] flex-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm portrait:text-[clamp(0.875rem,1.2vw,1rem)] text-foreground-secondary hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Auth Buttons + Theme Toggle - Desktop (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-3 portrait:gap-[clamp(0.75rem,1.2vw,1rem)]">
              {/* Theme Toggle - Desktop */}
              <button
                onClick={toggleTheme}
                className="ml-2 p-2 portrait:p-[clamp(0.5rem,0.8vw,0.75rem)] rounded-lg text-foreground-secondary hover:text-foreground hover:bg-white/5 transition-all"
                aria-label="Toggle theme"
              >
                {isLightTheme ? (
                  <Moon className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                ) : (
                  <Sun className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                )}
              </button>
            </div>

            {/* Mobile/Tablet Controls */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-foreground-secondary hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {isLightTheme ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground hover:text-foreground-secondary transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border/30 shadow-lg"
            >
              <div className="py-6 flex flex-col items-center gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-base text-foreground-secondary hover:text-foreground transition-colors py-2"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============================================ */}
      {/* MAIN CONTENT - NARROWER WIDTH */}
      {/* ============================================ */}
      <main className="flex-1 pt-[73px]">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 lg:py-20">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Privacy policy
            </h1>
            <p className="text-lg text-foreground-secondary mb-8">
              Last Updated: January 4, 2026
            </p>
            <div className="h-px bg-foreground/10" />
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-foreground-secondary text-lg mb-8 leading-relaxed">
              <span className="text-foreground font-medium">Lateral LTD</span> (&quot;We&quot;, &quot;Us&quot;, or &quot;Our&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal data when you use{" "}
              <span className="text-foreground font-medium">Lateral Teams Apps</span> (the &quot;Service&quot;).
            </p>

            <p className="text-foreground-secondary mb-12 leading-relaxed">
              For the purposes of the <strong className="text-foreground">UK General Data Protection Regulation (UK GDPR)</strong> and the Data Protection Act 2018, the Data Controller is Lateral LTD, located at 20-22 Wenlock Road, London, UK.
            </p>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Information We Collect</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">We collect the following types of information:</p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Account Data:</strong> Name, email address, billing address, and payment information (processed by our third-party payment providers).
                </li>
                <li>
                  <strong className="text-foreground">Usage Data:</strong> Information on how you access the Service (IP address, browser type, device info, login timestamps).
                </li>
                <li>
                  <strong className="text-foreground">Content Data:</strong> The text prompts (Inputs) you submit to the AI and the generated content (Outputs).
                </li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">2. How We Use Your Data</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">We use your data for the following purposes:</p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Service Delivery:</strong> To provide the AI generation features and manage your account.
                </li>
                <li>
                  <strong className="text-foreground">Billing:</strong> To process payments and prevent fraud.
                </li>
                <li>
                  <strong className="text-foreground">Improvement:</strong> To analyze usage trends and improve the platform&apos;s performance.
                </li>
                <li>
                  <strong className="text-foreground">Communication:</strong> To send you transactional emails (invoices, password resets) and, with your consent, marketing updates.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Legal Basis for Processing</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">We process your personal data based on:</p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Contract:</strong> Necessity to perform the contract (providing the Service).
                </li>
                <li>
                  <strong className="text-foreground">Legal Obligation:</strong> Compliance with tax and accounting laws.
                </li>
                <li>
                  <strong className="text-foreground">Legitimate Interest:</strong> Improving our services and ensuring security.
                </li>
                <li>
                  <strong className="text-foreground">Consent:</strong> For marketing communications (which you can withdraw at any time).  
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Data Sharing and Third Parties</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">
                We <strong className="text-foreground">do not sell</strong> your data. We may share data with trusted third-party processors who assist us in operating the Service, including:
              </p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Cloud Hosting Providers:</strong> (e.g., AWS, Google Cloud) for hosting the platform. 
                </li>
                <li>
                  <strong className="text-foreground">AI Model Providers:</strong> To generate the content requested by you.
                </li>
                <li>
                  <strong className="text-foreground">Payment Processors:</strong> (e.g., Stripe, PayPal) for secure billing.
                </li>
              </ul>
              <p className="text-foreground-secondary mt-4 leading-relaxed">
                All third parties are contractually bound to protect your data in accordance with GDPR standards.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">5. International Data Transfers</h2>
              <p className="text-foreground-secondary leading-relaxed">
                As a UK-based company, your data is stored primarily in the UK/EEA. If we transfer data outside the UK or EEA, we ensure appropriate safeguards are in place, such as the UK International Data Transfer Agreement (IDTA) or Standard Contractual Clauses (SCCs).     
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Your Rights</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">Under the UK GDPR, you have the right to:</p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Access:</strong> Request a copy of the personal data we hold about you.
                </li>
                <li>
                  <strong className="text-foreground">Rectification:</strong> Correct inaccurate data.
                </li>
                <li>
                  <strong className="text-foreground">Erasure:</strong> Request deletion of your data (&quot;Right to be forgotten&quot;).  
                </li>
                <li>
                  <strong className="text-foreground">Restriction:</strong> Restrict how we process your data.
                </li>
                <li>
                  <strong className="text-foreground">Portability:</strong> Receive your data in a structured format.
                </li>
              </ul>
              <p className="text-foreground mt-4">
                To exercise these rights, please contact us at{" "}
                <a href="mailto:info@lateralteams.com" className="text-accent hover:underline">
                  info@lateralteams.com
                </a>
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Data Retention</h2>
              <p className="text-foreground-secondary leading-relaxed">
                We retain your personal data only as long as necessary to fulfill the purposes outlined in this policy or to comply with legal obligations (e.g., tax records).
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Changes to This Policy</h2>
              <p className="text-foreground-secondary leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by email or via a prominent notice on our Service.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Contact Us</h2>
              <p className="text-foreground-secondary mb-4">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
                <ul className="space-y-2 text-foreground-secondary">
                  <li><strong className="text-foreground">Lateral LTD</strong></li>
                  <li>20-22 Wenlock Road, London, UK</li>
                  <li>
                    Email:{" "}
                    <a href="mailto:info@lateralteams.com" className="text-accent hover:underline">
                      info@lateralteams.com
                    </a>
                  </li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* ============================================ */}
      {/* FOOTER - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <footer className="relative border-t border-border pt-6 pb-0 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Top */}
          <div className="flex justify-end mb-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-sm text-foreground-secondary hover:text-foreground transition-colors flex items-center gap-1"
            >
              Back to top <span className="text-base">↑</span>
            </a>
          </div>

          {/* Footer content - responsive structure */}
          {/* Mobile & Tablet (< lg): Centered layout */}
          <div className="lg:hidden flex flex-col items-center gap-6 pb-6">
            <Link href="/" className="flex items-center">
              <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
            </Link>
            
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
              <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm text-foreground-secondary">
                20-22 Wenlock Road, London, UK
              </p>
              <p className="text-sm text-foreground-secondary">
                info@lateralteams.com
              </p>
            </div>
            <p className="text-sm text-foreground-muted text-center">
              © 2026 Lateral Teams · All rights reserved
            </p>
          </div>

          {/* Desktop (lg+): Multi-row layout */}
          <div className="hidden lg:block pb-6">
            {/* Top row: Logo + Main Nav */}
            <div className="flex items-center gap-8 mb-6">
              <Link href="/" className="flex items-center shrink-0">
                <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
              </Link>
            </div>
            {/* Bottom row: Legal + Company Info + Copyright */}
            <div className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-5 text-sm text-foreground-secondary">
                <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
                <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
                <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
                <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              </div>
              <div className="flex items-center gap-6 text-sm text-foreground-secondary">
                <span>20-22 Wenlock Road, London, UK</span>
                <span>·</span>
                <span>info@lateralteams.com</span>
              </div>
              <p className="text-sm text-foreground-muted shrink-0">
                © 2026 Lateral Teams · All rights reserved
              </p>
            </div>
          </div>
        </div>

        {/* Large Lateral text - 60% visible, 40% hidden - consistent on all screens */}
        <div className="relative h-[14vw] mt-4 overflow-hidden">
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-[-55%] text-[23vw] font-bold leading-none tracking-[0.02em] select-none pointer-events-none whitespace-nowrap"
            style={{ color: "var(--watermark-color)" }}
          >
            Lateral
          </span>
        </div>
      </footer>
    </div>
  );
}
